import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from "rxjs";
import {UntypedFormGroup} from "@angular/forms";
import {CostChangesEmitter, ResultDataCosts} from "../interfaces/result-data.interface";
import {ModeratAnfrage} from "../anfrage/moderat.anfrage";


@Component({
    selector: 'app-sanierung-invest',
    templateUrl: './sanierung-invest.component.html',
    standalone: false
})
export class SanierungInvestComponent implements OnInit, OnDestroy {

  /**
   * request object
   */
  @Input() anfrage: ModeratAnfrage;

  /**
   * the configuration of the costs we want to display
   */
  @Input() costs: ResultDataCosts;

  /**
   * form group for the inputs
   */
  @Input() form: UntypedFormGroup;

  /**
   * eventmemitter that is fired every time the costs change, so we can update the component.
   */
  @Input() costChanges: EventEmitter<CostChangesEmitter>;

  /**
   * toggle for the loading animation
   */
  @Input() loading: boolean = false;

  /**
   * Eventemitter if a value of the form changes
   */
  @Output() onCostChange: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Prefix for all textkeys
   */
  textkeyPrefix = 'moderat.sanierung.invest';

  /**
   * Timer to save the changes with a delay.
   */
  saveTimer: any;

  /**
   * remember the subscriptions so we can unsubscribe after leaving the component.
   */
  subscriptions: Subscription[] = [];

  /**
   * Flag to track if we're in edit mode
   */
  editMode: boolean = false;

  /**
   * Tracks which fields have been modified from their original values
   */
  modifiedFields: Set<string> = new Set<string>();

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    this.subscriptions.push(
      this.costChanges.subscribe((changes: CostChangesEmitter) => {
        // set (new) costs
        this.costs = changes.costs;

        if(changes.forceUpdate === true) {
          // show hint for changed
          if(this.modifiedFields.size > 0) {
            this.anfrage.showCostChangeHint = true;
          }

          // reset the values to the defaults and exit the edit mode
          this.resetValuesToDefaults();
          this.editMode = false;
        }
        this.updateModifiedFields();
      })
    );

    this.updateModifiedFields();

    // show cost change hint in case of cost changes when navigating back and forward
    if (this.anfrage.hasCostsChanged === true) {
      this.anfrage.showCostChangeHint = true;
      this.anfrage.hasCostsChanged = false;
    }
  }

  /**
   * Toggles edit mode on/off
   */
  toggleEditMode() {
    this.editMode = !this.editMode;
  }

  /**
   * Updates the modifiedFields set based on current form values
   */
  updateModifiedFields() {
    if (this.costs && this.costs.positions) {
      this.modifiedFields.clear();

      for (const measure of this.costs.positions) {
        if (measure.cost !== measure.default) {
          this.modifiedFields.add(measure.name);
        }
      }
    }
  }

  resetValuesToDefaults(): void {
    for (const measure of this.costs.positions) {
      if (this.form.get(measure.name)) {
        this.form.get(measure.name).setValue(measure.default);
      }
    }
  }

  /**
   * Resets all form values to null and triggers cost change
   */
  resetValues() {
    if (this.costs && this.costs.positions) {
      for (const measure of this.costs.positions) {
        if (this.form.get(measure.name)) {
          this.form.get(measure.name).setValue(null);
        }
      }
    }
    this.updateModifiedFields();
    this.triggerCostChange();
    this.editMode = false;
  }

  /**
   * On blur event of an input, save the changes with some delay
   */
  onInputBlur(): void {
    this.saveTimer = setTimeout(() => this.saveChanges(false), 200);
  }

  /**
   * Saves changes without exiting edit mode
   */
  saveChanges(exitEditModue: boolean = false) {
    // avoind double saving on blur and buttonclick
    clearTimeout(this.saveTimer);

    // check if the form is valid
    if(this.form.valid === false) {
      console.log("invalid", this.form.errors, this.form);
      return;
    }

    if (!this.form.dirty) {
      // do nothing, nothing has changed
      this.editMode = !exitEditModue;
      return;
    }

    this.anfrage.showCostChangeHint = false;

    this.updateModifiedFields();
    this.triggerCostChange();

    // mark form as np longer dirty
    this.form.markAsPristine();

    this.editMode = !exitEditModue;

    // save the case of cost changes
    this.anfrage.hasCostsChanged = true;
  }

  /**
   * Triggers cost change event
   */
  triggerCostChange() {
    this.onCostChange.emit();
  }

  /**
   * @inheritDoc
   */
  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
